import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { toast } from 'react-hot-toast';
import { 
  ArrowLeft, 
  Edit, 
  Calendar,
  User,
  Eye,
  TrendingUp,
  Tag,
  FileText,
  CheckCircle,
  Archive,
  Image as ImageIcon
} from 'lucide-react';
import { getBlogById, Blog, BlogStatus } from '../../services/blogApi';
import { useRbac } from '../../context';
import { isHtmlContent, richTextProseClassName } from '../../utils/richText';

export default function BlogDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useRbac();
  const [blog, setBlog] = useState<Blog | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      fetchBlog();
    }
  }, [id]);

  const fetchBlog = async () => {
    try {
      const response = await getBlogById(id!);
      if (response.success) {
        setBlog(response.data.blog);
      }
    } catch (error: any) {
      console.error('Error fetching blog:', error);
      toast.error(error.message || 'Failed to fetch blog');
      navigate('/admin/blogs');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: BlogStatus) => {
    switch (status) {
      case 'published':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'draft':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'archived':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: BlogStatus) => {
    switch (status) {
      case 'published':
        return <CheckCircle className="w-4 h-4" />;
      case 'draft':
        return <FileText className="w-4 h-4" />;
      case 'archived':
        return <Archive className="w-4 h-4" />;
      default:
        return null;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-gray-500">Loading blog...</p>
        </div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Blog Not Found</h2>
          <p className="text-gray-600 mb-4">The blog you're looking for doesn't exist or you don't have permission to view it.</p>
          <Button onClick={() => navigate('/admin/blogs')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blogs
          </Button>
        </div>
      </div>
    );
  }

  const author = typeof blog.author === 'object' ? blog.author : null;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/admin/blogs')}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">{blog.title}</h1>
            <p className="text-gray-500 mt-1">Blog Details</p>
          </div>
        </div>
        {user?.role === 'ADMIN' && (
          <Button onClick={() => navigate(`/admin/blogs/${blog._id}/edit`)}>
            <Edit className="w-4 h-4 mr-2" />
            Edit Blog
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Featured Image */}
          {blog.featuredImage && (
            <Card>
              <CardContent className="p-0">
                <img
                  src={blog.featuredImage}
                  alt={blog.title}
                  className="w-full h-96 object-cover rounded-t-lg"
                />
              </CardContent>
            </Card>
          )}

          {/* Blog Content */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2 mb-4">
                <Badge className={getStatusColor(blog.status)}>
                  <span className="flex items-center gap-1">
                    {getStatusIcon(blog.status)}
                    {blog.status}
                  </span>
                </Badge>
              </div>
              <CardTitle className="text-2xl">{blog.title}</CardTitle>
              {blog.excerpt && (
                <CardDescription className="text-lg mt-2">
                  {blog.excerpt}
                </CardDescription>
              )}
            </CardHeader>
            <CardContent>
              {isHtmlContent(blog.content) ? (
                <div
                  className={richTextProseClassName}
                  dangerouslySetInnerHTML={{ __html: blog.content }}
                />
              ) : (
                <div className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                  {blog.content}
                </div>
              )}
            </CardContent>
          </Card>

          {/* SEO Information */}
          {(blog.seoTitle || blog.seoDescription || (blog.seoKeywords && blog.seoKeywords.length > 0)) && (
            <Card>
              <CardHeader>
                <CardTitle>SEO Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {blog.seoTitle && (
                  <div>
                    <Label className="text-sm font-medium text-gray-700">SEO Title</Label>
                    <p className="text-gray-900 mt-1">{blog.seoTitle}</p>
                  </div>
                )}
                {blog.seoDescription && (
                  <div>
                    <Label className="text-sm font-medium text-gray-700">SEO Description</Label>
                    <p className="text-gray-900 mt-1">{blog.seoDescription}</p>
                  </div>
                )}
                {blog.seoKeywords && blog.seoKeywords.length > 0 && (
                  <div>
                    <Label className="text-sm font-medium text-gray-700">SEO Keywords</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {blog.seoKeywords.map((keyword) => (
                        <Badge key={keyword} variant="secondary">
                          {keyword}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Blog Info */}
          <Card>
            <CardHeader>
              <CardTitle>Blog Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2 text-sm">
                <User className="w-4 h-4 text-gray-400" />
                <span className="text-gray-600">Author:</span>
                <span className="font-medium">
                  {author ? author.username : 'Unknown'}
                </span>
              </div>

              {blog.publishedAt && (
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600">Published:</span>
                  <span className="font-medium">
                    {formatDate(blog.publishedAt)}
                  </span>
                </div>
              )}

              <div className="flex items-center gap-2 text-sm">
                <Calendar className="w-4 h-4 text-gray-400" />
                <span className="text-gray-600">Created:</span>
                <span className="font-medium">
                  {formatDate(blog.createdAt)}
                </span>
              </div>

              <div className="flex items-center gap-2 text-sm">
                <Calendar className="w-4 h-4 text-gray-400" />
                <span className="text-gray-600">Updated:</span>
                <span className="font-medium">
                  {formatDate(blog.updatedAt)}
                </span>
              </div>

              <div className="flex items-center gap-2 text-sm">
                <Eye className="w-4 h-4 text-gray-400" />
                <span className="text-gray-600">Views:</span>
                <span className="font-medium">{blog.views.toLocaleString()}</span>
              </div>

              <div className="flex items-center gap-2 text-sm">
                <TrendingUp className="w-4 h-4 text-gray-400" />
                <span className="text-gray-600">Likes:</span>
                <span className="font-medium">{blog.likes.toLocaleString()}</span>
              </div>
            </CardContent>
          </Card>

          {/* Tags */}
          {blog.tags && blog.tags.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Tag className="w-5 h-5" />
                  Tags
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {blog.tags.map((tag) => (
                    <Badge key={tag} variant="secondary">
                      {tag}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Slug */}
          <Card>
            <CardHeader>
              <CardTitle>URL Slug</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <code className="text-sm bg-gray-100 px-2 py-1 rounded flex-1 break-all">
                  {blog.slug}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(blog.slug);
                    toast.success('Slug copied to clipboard');
                  }}
                >
                  Copy
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
